import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { UserPlus, Trash2, Shield, User, Loader2 } from 'lucide-react';

export default function UserManagement({ onClose }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newUsername.trim() || !newPassword.trim()) {
      toast.error('Username and password required');
      return;
    }
    if (newPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    setCreating(true);
    try {
      await api.post('/users', { username: newUsername.trim(), password: newPassword });
      toast.success(`User "${newUsername.trim()}" created`);
      setNewUsername('');
      setNewPassword('');
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (userId, username) => {
    if (!window.confirm(`Delete user "${username}"? Their schedules will remain but become uneditable.`)) return;
    try {
      await api.delete(`/users/${userId}`);
      toast.success(`User "${username}" deleted`);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px]" data-testid="user-management-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <UserPlus className="w-5 h-5 text-indigo-600" />
            Manage Users
          </DialogTitle>
        </DialogHeader>

        {/* Create User Form */}
        <form onSubmit={handleCreate} className="space-y-3 border-b pb-5" data-testid="create-user-form">
          <p className="text-xs text-muted-foreground">
            New users can compose & send emails, manage their own schedules, and view analytics. They cannot manage sender accounts or other users.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Username</Label>
              <Input
                data-testid="new-username-input"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                placeholder="e.g. john.doe"
                className="h-9 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Password</Label>
              <Input
                data-testid="new-password-input"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min 4 characters"
                className="h-9 text-sm"
              />
            </div>
          </div>
          <Button
            type="submit"
            data-testid="create-user-button"
            disabled={creating || !newUsername.trim() || !newPassword.trim()}
            className="w-full gap-2 bg-indigo-600 hover:bg-indigo-700"
            size="sm"
          >
            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
            Create User
          </Button>
        </form>

        {/* Users List */}
        <div className="space-y-2 max-h-[300px] overflow-y-auto" data-testid="users-list">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : users.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No users yet</p>
          ) : (
            users.map((u) => (
              <div
                key={u.id}
                data-testid={`user-row-${u.username}`}
                className="flex items-center justify-between p-3 rounded-md border bg-secondary/30 hover:bg-secondary/60 transition-colors"
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                    u.role === 'superadmin' ? 'bg-amber-100 dark:bg-amber-950/40' : 'bg-indigo-100 dark:bg-indigo-950/40'
                  }`}>
                    {u.role === 'superadmin'
                      ? <Shield className="w-3.5 h-3.5 text-amber-600" />
                      : <User className="w-3.5 h-3.5 text-indigo-600" />
                    }
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{u.username}</p>
                    <p className="text-xs text-muted-foreground">
                      {u.created_by === 'system' ? 'System' : `by ${u.created_by}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant={u.role === 'superadmin' ? 'default' : 'outline'} className="text-xs">
                    {u.role === 'superadmin' ? 'Admin' : 'User'}
                  </Badge>
                  {u.role !== 'superadmin' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`delete-user-${u.username}`}
                      onClick={() => handleDelete(u.id, u.username)}
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
